import React, { useMemo } from "react";
import {
  IconChevronLeft, IconChevronRight, IconChevronsLeft, IconChevronsRight,
} from "@tabler/icons-react";

import PaginationTile from "./PaginationTile";

export const GAP_MARKER = Symbol("gap");
export enum PaginationType {
  SIMPLE = "SIMPLE",
  FAST = "FAST",
  LONG = "LONG",
}

export interface PaginationProps {
  /**
   * The type of pagination to display.
   * - If SIMPLE, the page number and left and right arrows are displayed.
   * - If FAST, the jump to start and end buttons are displayed as well as everything in simple.
   * - If LONG, the current, first, last pages are always displayed, and the number of visible pages is determined by the maxVisibleCount prop.
   *   It acts more like a desktop pagination. On mobile, this falls back to FAST.
   */
  type?: PaginationType | `${PaginationType}`;
  count: number;
  /** This is 0-indexed, but will display as 1-indexed. I.e. 0 is page 1. */
  value: number;
  onChange: (page: number) => void;
  /**  The number of always visible pages either side of the current page */
  boundaryCount?: number;
  disabled?: boolean;

}

const Pagination: React.FC<PaginationProps> = ({
  type = PaginationType.SIMPLE,
  value,
  count,
  onChange,
  boundaryCount = 1,
  disabled,
}) => {
  const numberChunksToDisplay = (() => {
    if (type === PaginationType.SIMPLE || type === PaginationType.FAST) {
      return [value + 1];
    }

    let startPage;
    let endPage;
    const currPage = value + 1;
    const maxCount = 4 + boundaryCount * 2 + 1;

    if (count <= maxCount) {
      startPage = 1;
      endPage = count;
    } else if (currPage <= boundaryCount + 3) {
      startPage = 1;
      endPage = (2 * boundaryCount) + 3;
    } else if (currPage >= count - (boundaryCount + 2)) {
      startPage = count - ((2 * boundaryCount) + 2);
      endPage = count;
    } else {
      startPage = currPage - boundaryCount;
      endPage = currPage + boundaryCount;
    }

    const pages = [] as (number | typeof GAP_MARKER)[];
    if (startPage > 1) {
      pages.push(1);
    }

    if (startPage > 2) {
      pages.push(GAP_MARKER);
    }

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(i);
    }

    if (endPage < count - 2) {
      pages.push(GAP_MARKER);
    }

    if (endPage < count - 1) {
      pages.push(count);
    }

    return pages;
  })();

  return (
    <div className="flex flex-row items-center">
      {(type === PaginationType.LONG || type === PaginationType.FAST) && (
        <PaginationTile
          disabled={disabled || value <= 0}
          onClick={() => onChange(0)}
          sides="left"
        >
          <IconChevronsLeft />
        </PaginationTile>
      )}
      <PaginationTile
        disabled={disabled || value <= 0}
        onClick={() => onChange(value - 1)}
        sides={type === PaginationType.SIMPLE ? "left" : "none"}
      >
        <IconChevronLeft />
      </PaginationTile>
      {numberChunksToDisplay.map((page, i) => {
        if (page === GAP_MARKER) {
          return (
            <PaginationTile
              key={`...-${i}`}
              hideOnMobile
            >
              ...
            </PaginationTile>
          );
        }
        return (
          <PaginationTile
            key={page}
            active={page === value + 1}
            hideOnMobile={
              page !== value + 1
            }
            disabled={disabled}
            onClick={() => onChange(page - 1)}
          >
            {page}
          </PaginationTile>
        );
      })}
      <PaginationTile
        disabled={disabled || value >= count - 1}
        onClick={() => onChange(value + 1)}
        sides={type === PaginationType.SIMPLE ? "right" : "none"}
      >
        <IconChevronRight />
      </PaginationTile>
      {(type === PaginationType.LONG || type === PaginationType.FAST) && (
        <PaginationTile
          disabled={disabled || value >= count - 1}
          onClick={() => onChange(count - 1)}
          sides="right"
        >
          <IconChevronsRight />
        </PaginationTile>
      )}
    </div>
  );
};

export default Pagination;
